import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Date      : $Date: 2026-08-20 08:02:31 +0200 (Thu, 20 Aug 2026) $
//  Author    : Olli Powroznik
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 164960 $
//  Purpose   : Responsive toolbar with group collapsing and overflow scrolling
// =============================================================================
// ---Third-party imports
import clsx from 'clsx';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
// ---Framework imports
import { ChildPopupRegistry, DebugFeature, XMenu } from 'uix/components';
import { useResizeObserver, useSelector, useUserSetting } from 'uix/hooks';
import { VIcon } from 'uix/graphics';
import { Sel } from 'uix/registries';
// ---Local imports
import S from './index.css';
import { useResponsiveLayout } from './hooks';
import { countActive, flattenChildren, keptItemIndices, readGroups, trimSeparators } from './utils';
const Toolbar = ({ responsive = false, attached = false, titled = true, ribbons = false, vertical = false, children, floated, disabled, colored = false, size = 'default', className, ...props })=>{
    let [headerStyle] = useUserSetting('headerStyle');
    if (headerStyle == 'compact') ribbons = false;
    let resolveAttach = ()=>{
        if (attached === true && !vertical) return 'top';
        if (vertical && attached === true) return 'left';
        return attached;
    };
    let attachClass = {
        top: S.topAttach,
        bottom: S.bottomAttach,
        left: S.leftAttach,
        right: S.rightAttach,
        notAttach: S.notAttach
    }[resolveAttach()] || S.notAttach;
    let sizeClass = {
        mini: S.mini,
        tiny: S.tiny,
        small: S.small,
        default: S.default,
        large: S.large,
        huge: S.huge
    }[size];
    let menuClass = clsx(className, S.toolbar, attachClass, sizeClass, ribbons && S.ribbons, !titled && S.hideTitle, vertical && S.vertical, floated === 'right' && S.floated, colored && S.colored);
    let menuProps = {
        vertical,
        attached: attached ? true : false,
        floated,
        size: size === 'default' ? null : size,
        ...props
    };
    if (!responsive) {
        return /*#__PURE__*/ _jsx("div", {
            style: {
                width: 'inherit'
            },
            children: /*#__PURE__*/ _jsx(XMenu, {
                ...menuProps,
                className: menuClass,
                children: children
            })
        });
    }
    return /*#__PURE__*/ _jsx(ResponsiveToolbar, {
        menuProps: menuProps,
        menuClass: menuClass,
        overflowClass: S.overflowing,
        disabled: disabled,
        ribbons: ribbons,
        sizeSignature: `${size}|${ribbons}|${titled}|${vertical}`,
        children: children
    });
};
const Group = ({ position, separator = true, children, title, style, show = true })=>{
    if (!show) return null;
    return /*#__PURE__*/ _jsxs("div", {
        style: style,
        className: clsx(S.group, position === 'right' && S.right),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: S.menu,
                children: [
                    children,
                    separator && /*#__PURE__*/ _jsx(XMenu.Separator, {
                        className: clsx(!separator && S.hidden, S.separator)
                    })
                ]
            }),
            title && /*#__PURE__*/ _jsx("span", {
                className: S.title,
                children: title
            })
        ]
    });
};
const ItemList = ({ children })=>/*#__PURE__*/ _jsx("div", {
        className: S.itemList,
        children: children
    });
const SCROLL_EDGE_RESERVE = 45;
const SCROLL_FALLBACK_OFFSET = 200;
const SCROLL_DURATION = 320;
const MeasureCell = ({ gi, ii, onCell, signature, children })=>{
    let ref = useResizeObserver({
        onResize: useCallback(({ width })=>onCell(gi, ii, width), [
            gi,
            ii,
            onCell,
            signature
        ])
    });
    return /*#__PURE__*/ _jsx("span", {
        ref: ref,
        className: S.cell,
        children: children
    });
};
const MeasureProbe = ({ onWidth, children })=>{
    let ref = useResizeObserver({
        onResize: useCallback(({ width })=>onWidth(width), [
            onWidth
        ])
    });
    return /*#__PURE__*/ _jsx("span", {
        ref: ref,
        "aria-hidden": true,
        className: S.probe,
        children: children
    });
};
const OverflowScrollButtons = ({ scrollRef, renderLeft, renderRight, classes = {} })=>{
    let [atStart, setAtStart] = useState(true);
    let [atEnd, setAtEnd] = useState(false);
    let rafRef = useRef(0);
    let sync = useCallback(()=>{
        let el = scrollRef.current;
        if (!el) return;
        setAtStart(el.scrollLeft <= 0);
        setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);
    }, [
        scrollRef
    ]);
    useEffect(()=>{
        let el = scrollRef.current;
        if (!el) return;
        sync();
        el.addEventListener('scroll', sync);
        return ()=>{
            el.removeEventListener('scroll', sync);
            cancelAnimationFrame(rafRef.current);
        };
    }, [
        sync
    ]);
    let easeInOutQuad = (t)=>t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
    let scroll = (dir)=>{
        let el = scrollRef.current;
        if (!el) return;
        let step = (el.clientWidth || SCROLL_FALLBACK_OFFSET) - SCROLL_EDGE_RESERVE;
        let from = el.scrollLeft;
        let to = Math.max(0, Math.min(from + (dir === 'right' ? step : -step), el.scrollWidth - el.clientWidth));
        cancelAnimationFrame(rafRef.current);
        let startTs = null;
        let frame = (ts)=>{
            if (startTs === null) startTs = ts;
            let p = Math.min(1, (ts - startTs) / SCROLL_DURATION);
            el.scrollLeft = from + (to - from) * easeInOutQuad(p);
            if (p < 1) rafRef.current = requestAnimationFrame(frame);
        };
        rafRef.current = requestAnimationFrame(frame);
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                onClick: atStart ? null : ()=>scroll('left'),
                className: clsx(classes.button, classes.left, atStart && classes.disabled),
                children: renderLeft({
                    disabled: atStart
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                onClick: atEnd ? null : ()=>scroll('right'),
                className: clsx(classes.button, classes.right, atEnd && classes.disabled),
                children: renderRight({
                    disabled: atEnd
                })
            })
        ]
    });
};
const More = ({ children, icon, caption, activeItems, prominent, title, vertical = false, position = 'bottom left', closeOnChildClick = false, onClick: onClickProp, ...props })=>{
    let i18n = useSelector(Sel.i18n);
    const [childOpenCount, setChildOpenCount] = useState(0);
    const closeCallbacks = useRef(new Set());
    const isMoreOpenRef = useRef(false);
    const registry = useMemo(()=>({
            register: (closeFn)=>{
                setChildOpenCount((c)=>c + 1);
                if (closeFn) closeCallbacks.current.add(closeFn);
            },
            unregister: (closeFn)=>{
                setChildOpenCount((c)=>Math.max(0, c - 1));
                if (closeFn) closeCallbacks.current.delete(closeFn);
            },
            closeAll: ()=>{
                closeCallbacks.current.forEach((fn)=>fn());
                closeCallbacks.current.clear();
                setChildOpenCount(0);
            }
        }), []);
    const handleMoreClick = useCallback(()=>{
        if (isMoreOpenRef.current) registry.closeAll();
        isMoreOpenRef.current = !isMoreOpenRef.current;
        onClickProp?.();
    }, [
        registry,
        onClickProp
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        title: title || i18n.get('more'),
        className: S.moreItem,
        children: [
            /*#__PURE__*/ _jsx(XMenu.PopupItem, {
                basic: true,
                renderAlways: true,
                position: position,
                className: clsx(S.more, icon && S.customIcon, prominent && S.prominent),
                icon: icon ? icon : 'ellipsis horizontal',
                closeOnChildClick: closeOnChildClick,
                prominent: prominent,
                closeOnPopupDescendantClick: false,
                keepOpen: childOpenCount > 0,
                onClick: handleMoreClick,
                ...props,
                children: /*#__PURE__*/ _jsx(ChildPopupRegistry.Provider, {
                    value: registry,
                    children: /*#__PURE__*/ _jsx(XMenu, {
                        vertical: vertical,
                        attached: false,
                        className: clsx(S.toolbar, S.group, S.more, vertical && S.popupMenu),
                        children: children
                    })
                })
            }),
            activeItems > 0 && /*#__PURE__*/ _jsx("span", {
                className: S.badge,
                children: activeItems
            })
        ]
    });
};
const ResponsiveGroup = ({ g, gi, entry, measuring, disabled, reporters, signature })=>{
    let { position, separator = true, title, icon, style } = g.props;
    let total = g.items.length;
    let collapsed = !measuring && (entry?.collapsed || false);
    let visibleCount = measuring || !entry ? total : entry.visibleCount;
    let visible = collapsed ? [] : g.items.slice(0, visibleCount);
    let hidden = collapsed ? g.items : g.items.slice(visibleCount);
    let flatHidden = useMemo(()=>flattenChildren(hidden), [
        hidden
    ]);
    let hiddenActiveCount = useMemo(()=>countActive(hidden), [
        hidden
    ]);
    let visibleIdx = useMemo(()=>measuring ? visible.map((_, i)=>i) : keptItemIndices(visible), [
        measuring,
        visible
    ]);
    let clonedVisibleItems = useMemo(()=>visibleIdx.map((i)=>/*#__PURE__*/ React.cloneElement(visible[i], {
                disabled
            })), [
        visibleIdx,
        visible,
        disabled
    ]);
    let menuRef = useResizeObserver({
        onResize: useCallback(({ width })=>collapsed ? reporters.onCollapsedGroup(gi, width) : reporters.onMenu(gi, width), [
            gi,
            collapsed,
            reporters,
            signature
        ])
    });
    let titleRef = useResizeObserver({
        onResize: useCallback(({ width })=>reporters.onTitle(gi, width), [
            gi,
            reporters,
            signature
        ])
    });
    let groupRef = useResizeObserver({
        types: [
            'offset'
        ],
        onResize: useCallback(({ offset })=>reporters.onGroup(gi, offset?.width ?? 0), [
            gi,
            reporters,
            signature
        ])
    });
    return /*#__PURE__*/ _jsxs("div", {
        ref: groupRef,
        style: style,
        className: clsx(S.group, position === 'right' && S.right),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: S.menu,
                children: /*#__PURE__*/ _jsx("div", {
                    ref: menuRef,
                    className: S.menuRow,
                    children: collapsed ? /*#__PURE__*/ _jsx(More, {
                        prominent: true,
                        icon: icon,
                        disabled: disabled || flatHidden.every((c)=>c?.props?.disabled),
                        activeItems: countActive(g.items),
                        children: trimSeparators(flatHidden)
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            visibleIdx.map((i, idx)=>/*#__PURE__*/ _jsx(MeasureCell, {
                                    gi: gi,
                                    ii: i,
                                    onCell: reporters.onCell,
                                    signature: signature,
                                    children: clonedVisibleItems[idx]
                                }, i)),
                            !measuring && hidden.length > 0 && /*#__PURE__*/ _jsx(More, {
                                disabled: disabled,
                                activeItems: hiddenActiveCount,
                                children: trimSeparators(flatHidden)
                            }),
                            /*#__PURE__*/ _jsx(XMenu.Separator, {
                                className: clsx(!separator && S.hidden, S.separator)
                            })
                        ]
                    })
                })
            }),
            title && /*#__PURE__*/ _jsx("span", {
                className: S.title,
                children: /*#__PURE__*/ _jsx("span", {
                    ref: titleRef,
                    className: S.titleText,
                    children: title
                })
            })
        ]
    });
};
const ResponsiveToolbar = ({ menuProps, menuClass, overflowClass, disabled, ribbons, sizeSignature, children })=>{
    let isDebugOn = useSelector(Sel.isDebugOn);
    let groups = useMemo(()=>readGroups(children, isDebugOn), [
        children,
        isDebugOn
    ]);
    let signature = useMemo(()=>`${sizeSignature}#${groups.map((g)=>g.items.length).join('|')}`, [
        groups,
        sizeSignature
    ]);
    let { setContainerRef, containerRef, measuring, layout, overflow, reporters } = useResponsiveLayout({
        groups,
        signature,
        ribbons
    });
    let renderGroup = (g, gi)=>{
        let node = /*#__PURE__*/ _jsx("div", {
            style: g.props.style,
            className: clsx(S.responsive, g.props.position === 'right' && S.right),
            children: /*#__PURE__*/ _jsx(ResponsiveGroup, {
                g: g,
                gi: gi,
                entry: layout?.[gi],
                measuring: measuring,
                disabled: disabled,
                reporters: reporters,
                signature: signature
            })
        }, g.key);
        return g.debug ? /*#__PURE__*/ _jsx(DebugFeature, {
            children: node
        }, g.key) : node;
    };
    return /*#__PURE__*/ _jsx("div", {
        style: {
            width: '100%'
        },
        children: /*#__PURE__*/ _jsx(XMenu, {
            ...menuProps,
            className: clsx(menuClass, overflow && overflowClass),
            children: /*#__PURE__*/ _jsxs("div", {
                className: S.scrollFrame,
                children: [
                    overflow && !measuring && /*#__PURE__*/ _jsx(OverflowScrollButtons, {
                        scrollRef: containerRef,
                        renderLeft: ({ disabled: d })=>/*#__PURE__*/ _jsx(VIcon, {
                                disabled: d,
                                name: "chevron left"
                            }),
                        renderRight: ({ disabled: d })=>/*#__PURE__*/ _jsx(VIcon, {
                                disabled: d,
                                name: "chevron right"
                            }),
                        classes: {
                            button: S.scrollButton,
                            left: S.left,
                            right: S.right,
                            disabled: S.disabled
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        ref: setContainerRef,
                        className: clsx(S.scrollContainer, measuring && S.measuring),
                        children: [
                            /*#__PURE__*/ _jsx(MeasureProbe, {
                                onWidth: reporters.onMore,
                                children: /*#__PURE__*/ _jsx(More, {
                                    activeItems: 0
                                })
                            }),
                            /*#__PURE__*/ _jsx(MeasureProbe, {
                                onWidth: reporters.onCollapsed,
                                children: /*#__PURE__*/ _jsx(More, {
                                    prominent: true,
                                    activeItems: 0
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": true,
                                className: S.heightAnchor,
                                children: /*#__PURE__*/ _jsx(More, {
                                    prominent: true,
                                    activeItems: 0
                                })
                            }),
                            groups.map(renderGroup)
                        ]
                    })
                ]
            })
        })
    });
};
function ToolbarSeparator({ className, ...props }) {
    return /*#__PURE__*/ _jsx(XMenu.Separator, {
        className: clsx(S.separator, className),
        ...props
    });
}
// ---Exports
Toolbar.Group = Group;
Toolbar.Item = XMenu.Item;
Toolbar.ItemList = ItemList;
Toolbar.More = More;
Toolbar.Separator = ToolbarSeparator;
ToolbarSeparator.displayName = 'ToolbarSeparator';
Group.displayName = 'ToolbarGroup';
ItemList.displayName = 'ToolbarItemList';
More.displayName = 'ToolbarMore';
export { Toolbar, MeasureCell, MeasureProbe, OverflowScrollButtons, More };
